import { ProHistoricalTransaction } from './ProHistoricalTransaction'
import { collection } from 'infra/collection'
import { CancellationToken } from 'infra/cancellation'
import { DateRange } from 'infra/FilterControls/DateRange/DateRangeFilter'
import { ProHistoricalTransactionResource } from './dtos/pro-transactions'
import { BigMoneyPayoutsAPI } from './API'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { PrimeJob, FetchJob } from 'jobs/JobStore'
import { CursorPageQuery, CursorPaged, Id } from 'types/definitions'
import {
  TransactionSourceType,
  serializeTransactionSourceType,
} from '../TransactionSourceType'

/**
 * Manages pro-facing payouts.
 */
export class ProPayoutStore {
  /**
   * Historical transactions.
   */
  readonly transactions = collection<
    ProHistoricalTransactionResource,
    ProHistoricalTransaction
  >({
    create: () =>
      new ProHistoricalTransaction(this.primeWorkspace, this.primeJob),
  })

  /**
   * Constructor.
   */
  constructor(
    private readonly api: BigMoneyPayoutsAPI,
    private readonly primeWorkspace: PrimeWorkspace,
    private readonly primeJob: PrimeJob,
    private readonly fetchJob: FetchJob,
  ) {}

  /**
   * Lists historical transactions.
   *
   * @param query
   * @param paging
   * @param cancellationToken
   */
  async listHistoricalTransactions(
    query: ListHistoricalTransactionsQuery,
    paging: CursorPageQuery,
    cancellationToken = CancellationToken.default,
  ): Promise<CursorPaged<ProHistoricalTransaction>> {
    const response = await this.api.transactions.listHistoricalTransactions(
      {
        page_size: paging.limit,
        page_token: paging.after,
        workspace_ids: query.workspaceIds?.length
          ? query.workspaceIds?.join(',')
          : undefined,
        date_completed_from: query.dateCompleted?.from ?? undefined,
        date_completed_to: query.dateCompleted?.to ?? undefined,
        transaction_source:
          query.transactionSource
            ?.map(serializeTransactionSourceType)
            .join(',') ?? undefined,
        search_text: query.searchText,
        order_by: 'date_completed desc',
      },
      cancellationToken,
    )

    // For job-related transactions, fetch the jobs.
    // This is because `primeJob` does not do a set-and-fetch like the other
    // prime methods.
    await Promise.all(
      response.data.map((dto) => this._fetchRelatedTransactionData(dto)),
    )

    const page = CursorPaged.fromAipCursorPaged(response)
    return CursorPaged.map(page, this.transactions.set)
  }

  /**
   * Downloads the historical transactions as a CSV.
   *
   * @param query
   */
  async downloadHistoricalTransactionsAsCsv(
    query: DownloadHistoricalTransactionsAsCsvQuery = {},
  ): Promise<ReadableStream<Uint8Array>> {
    return await this.api.transactions.downloadHistoricalTransactionsAsCsv({
      workspace_ids: query.workspaceIds?.length
        ? query.workspaceIds?.join(',')
        : undefined,
      search_text: query.searchText,
      transaction_source:
        query.transactionSource
          ?.map(serializeTransactionSourceType)
          .join(',') ?? undefined,
      date_completed_from: query.dateCompleted?.from ?? undefined,
      date_completed_to: query.dateCompleted?.to ?? undefined,
    })
  }

  /**
   * Fetches the current user's payout summary.
   */
  async fetchPayoutSummary(): Promise<ProPayoutSummary> {
    const response = await this.api.analytics.getProPayoutSummary()
    return {
      earningsYearToDate: response.earnings_ytd,
      lifetimeEarnings: response.earnings_lifetime,
      pendingEarnings: response.pending,
    }
  }

  /**
   * Fetches related transaction data for the given transaction.
   * @param dto
   * @private
   */
  private async _fetchRelatedTransactionData(
    dto: ProHistoricalTransactionResource,
  ) {
    if (dto.job_id) {
      try {
        await this.fetchJob(dto.job_id)
      } catch (err) {
        // Some jobs may no longer exist or have been archived.
        // This is expected, so we'll ignore it here.
        console.warn(
          `Unable to retrieve associated job ${dto.job_id} for transaction`,
          err,
        )
      }
    }
  }
}

/**
 * Lists historical transactions.
 */
export interface ListHistoricalTransactionsQuery {
  workspaceIds?: Id[] | null
  dateCompleted?: DateRange | null
  transactionSource?: TransactionSourceType[] | null
  searchText?: string
}

/**
 * Downloads historical transactions as a CSV.
 */
export interface DownloadHistoricalTransactionsAsCsvQuery {
  workspaceIds?: Id[] | null
  recipientUserId?: Id
  dateCompleted?: DateRange | null
  transactionSource?: TransactionSourceType[] | null
  searchText?: string
}

/**
 * Summary of a Pro's payouts.
 */
export interface ProPayoutSummary {
  earningsYearToDate: number
  pendingEarnings: number
  lifetimeEarnings: number
}

export const ProPayoutSummary = {
  /**
   * Returns a default value for the pro payout summary.
   */
  default(): ProPayoutSummary {
    return {
      pendingEarnings: 0,
      lifetimeEarnings: 0,
      earningsYearToDate: 0,
    }
  },
}
